"use client";

import React, { useId, useRef, useLayoutEffect } from "react";
import { motion, useInView } from "motion/react";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import Image from "next/image";
import { useGSAP } from "@/components/providers/gsap-provider";
import { useLocale, useTranslations } from "next-intl";

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: { opacity: 1 },
  show: { opacity: 1, transition: { staggerChildren: 0.12 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const Community = () => {
  const t = useTranslations("community");
  const locale = useLocale();
  const svgId = useId().replace(/:/g, "");
  const radialGradientId = `${svgId}-radial-gradient`;
  const radialGradient2Id = `${svgId}-radial-gradient-2`;
  const radialGradient3Id = `${svgId}-radial-gradient-3`;
  const radialGradient4Id = `${svgId}-radial-gradient-4`;
  const svgRef = useRef(null);
  const pathOneRef = useRef(null);
  const pathTwoRef = useRef(null);
  const pathThreeRef = useRef(null);
  const pathFourRef = useRef(null);
  const sectionRef = useRef(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(contentRef, { once: true, margin: "0px 0px -80px 0px" });
  const { gsap } = useGSAP();

  // Use useLayoutEffect for GSAP animations to ensure they run before browser paint
  useLayoutEffect(() => {
    // Create a floating/breathing animation effect for the SVG background
    if (!svgRef.current) return;

    // Store refs in variables to prevent cleanup issues
    const svgElement = svgRef.current;
    const pathElements = [
      pathOneRef.current,
      pathTwoRef.current,
      pathThreeRef.current,
      pathFourRef.current,
    ].filter(Boolean);

    // Create a timeline for better control
    const tl = gsap.timeline({
      defaults: {
        ease: "sine.inOut",
      },
    });

    // Initial fade-in animation
    tl.fromTo(svgElement, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1 });

    // Create more noticeable animations for each path
    pathElements.forEach((path, index) => {
      // Animate y-position and opacity with different values for each path
      gsap.to(path, {
        y: index % 2 === 0 ? 20 : -20,
        opacity: 0.6 + index * 0.1,
        duration: 3 + index * 0.5,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        delay: index * 0.4,
      });
    });

    return () => {
      // Clean up animations when component unmounts
      gsap.killTweensOf([...pathElements, svgElement]);
      tl.kill();
    };
  }, [gsap]);

  return (
    <section
      ref={sectionRef}
      className="section relative pt-[30px] pb-[50px] lg:pt-[50px] lg:pb-[80px] overflow-visible section--community"
    >
      {/* SVG Background */}
      <div className="absolute w-full h-full top-0 left-0 overflow-visible -z-10 pointer-events-none">
        <svg
          ref={svgRef}
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 2137 1630.72"
          preserveAspectRatio="none"
          className="absolute top-[35%] lg:top-2/4 xl:top-[calc(50%-60px)] left-2/4 -translate-x-2/4 -translate-y-2/4 w-screen md:min-w-[900px] h-auto community--shape"
          style={{
            pointerEvents: "none",
            maxWidth: "none",
            width: "120%" /* Makes sure it's always wider than the viewport */,
            height: "auto",
            minHeight: "600px",
            transformOrigin: "center center",
          }}
        >
          <defs>
            <radialGradient
              id={radialGradientId}
              cx="815.51"
              cy="1078.54"
              fx="815.51"
              fy="1078.54"
              r="1"
              gradientTransform="translate(-768437.34 1703622.67) scale(943.55 -1578.81)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#8752fa" />
              <stop offset="1" stopColor="#8752fa" stopOpacity="0" />
            </radialGradient>
            <radialGradient
              id={radialGradient2Id}
              cx="815.51"
              cy="1078.6"
              fx="815.51"
              fy="1078.6"
              r="1"
              gradientTransform="translate(-768540.79 1277173.66) scale(943.67 -1183.35)"
              xlinkHref={`#${radialGradientId}`}
            />
            <radialGradient
              id={radialGradient3Id}
              cx="815.51"
              cy="1078.67"
              fx="815.51"
              fy="1078.67"
              r="1"
              gradientTransform="translate(-768540.79 973269.82) scale(943.67 -901.53)"
              xlinkHref={`#${radialGradientId}`}
            />
            <radialGradient
              id={radialGradient4Id}
              cx="815.42"
              cy="1078.89"
              fx="815.42"
              fy="1078.89"
              r="1"
              gradientTransform="translate(-647855.28 574596.26) scale(795.78 -531.83)"
              xlinkHref={`#${radialGradientId}`}
            />
          </defs>
          <g opacity="0.5">
            <path
              ref={pathOneRef}
              fill={`url(#${radialGradientId})`}
              d="M0,0c694.41,900.17,1453.87,900.17,2136.71,0v1630.72C1446.87,742.08,678.99,742.08,0,1630.72V0Z"
            />
            <path
              ref={pathTwoRef}
              fill={`url(#${radialGradient2Id})`}
              d="M0,204.23c686.31,670.12,1445.87,670.12,2137,0v1222.26C1447.07,760.43,679.08,760.43,0,1426.49V204.23Z"
            />
            <path
              ref={pathThreeRef}
              fill={`url(#${radialGradient3Id})`}
              d="M0,349.78c693.06,528.49,1452.62,528.49,2137,0v931.17c-689.93-507.43-1457.92-507.43-2137,0V349.78Z"
            />
            <path
              ref={pathFourRef}
              fill={`url(#${radialGradient4Id})`}
              d="M167.45,540.71c584.44,311.76,1224.97,311.76,1802.1,0v549.31c-581.81-299.34-1229.44-299.34-1802.1,0v-549.31Z"
            />
          </g>
        </svg>
      </div>

      <div className="container relative z-20">
        <motion.div
          ref={contentRef}
          className="grid md:grid-cols-2 gap-[30px]"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          {/* GRID ITEM */}
          <motion.div variants={itemVariants} className="grid__item">
            <AnimatedCard
              className="rounded-[20px] lg:rounded-[25px] overflow-hidden"
              circleClassName="w-[100px] h-[100px]"
            >
              <BorderWrapper
                wrapperClass="rounded-[20px] lg:rounded-[25px]"
                className="rounded-[20px] lg:rounded-[25px] flex flex-col bg-gradient-to-b from-[#121124] to-[#0e0821] px-[10px] md:px-[16px] lg:px-[20px] pt-[30px] pb-[15px] md:py-[24px] lg:py-[30px] gap-[30px] relative overflow-hidden"
              >
                <div
                  className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                  />
                <div className="lg:max-w-[450px] space-y-[10px] px-[20px] lg:px-[25px]">
                  <h3 className="text-[24px] md:text-[30px] lg:text-[36px] font-semibold leading-[1.2]">
                    {t("howWeGrow.title")}
                  </h3>
                  <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                    {t("howWeGrow.description")}
                  </p>
                </div>

                <div className="relative w-full h-[200px] md:h-[280px] lg:h-[340px] rounded-[15px] lg:rounded-[20px] overflow-hidden">
                  <Image
                    src={`/assets/photos/growth-${locale}.webp`}
                    alt="Sustainable growth"
                    width={1092}
                    height={674}
                    sizes="(min-width: 768px) 50vw, 100vw"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </BorderWrapper>
            </AnimatedCard>
          </motion.div>
          {/* GRID ITEM */}
          <motion.div variants={itemVariants} className="grid__item lg:translate-y-[25px]">
            <AnimatedCard
              className="rounded-[20px] lg:rounded-[25px] overflow-hidden"
              circleClassName="w-[100px] h-[100px]"
            >
              <BorderWrapper
                wrapperClass="rounded-[20px] lg:rounded-[25px]"
                className="rounded-[20px] lg:rounded-[25px] flex flex-col-reverse lg:flex-col bg-gradient-to-b from-[#121124] to-[#0e0821] px-[10px] md:px-[16px] lg:px-[20px] pt-[30px] pb-[15px] md:py-[24px] lg:py-[30px] gap-[30px] relative overflow-hidden"
              >
                <div
                  className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                  />
                <div className="relative w-full h-[200px] md:h-[280px] lg:h-[340px] rounded-[15px] lg:rounded-[20px] overflow-hidden">
                  <Image
                    src={`/assets/photos/responsibility-${locale}.webp`}
                    alt="Social responsibility"
                    width={1092}
                    height={674}
                    sizes="(min-width: 768px) 50vw, 100vw"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>

                <div className="max-w-[450px] space-y-[10px] md:mb-[30px] px-[20px] lg:px-[25px]">
                  <h3 className="text-[24px] md:text-[30px] lg:text-[36px] font-semibold leading-[1.2]">
                    {t("sustainability.title")}
                  </h3>
                  <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                    {t("sustainability.description")}
                  </p>
                </div>
              </BorderWrapper>
            </AnimatedCard>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Community;
